import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { LocaleLink } from '../localeLink/LocaleLink';
import * as styles from './breadcrumb.module.scss';
import { GetTranslatesQuery } from '../../../../graphql-types';
import { Path, usePageContext } from '../../../hooks/contexts/usePageContext';

interface IBreadcrumbProps {
  pathsInput?: Path[];
}

const pathnameToObjectWithHomeAndBack = (
  pathname: Path[],
  lang: string,
  home: { label: string; url: string },
  back: { label: string; url: string }
) => {
  const pathNameObject = pathname.reduce(
    (acc: { label: string; url: string }[], current, index) => {
      if (index > 0) {
        acc.push({
          label: current.label?.[lang],
          url: `${acc[index - 1].url}${current.url}`,
        });
      } else {
        acc.push({
          label: current.label?.[lang],
          url: `${current.url}`,
        });
      }
      return acc;
    },
    []
  );
  pathNameObject.unshift(back, home);
  return pathNameObject;
};

/**
 * for use it you have to setup the paths in the context in gatsby node for template pages
 * or in the classique page -> see https://gitlab.com/210pixels/soixanteseize/tours/tours-webapp/-/blob/6b686122a672a6a4875777db5b42fc70c48d9867/src/pages/preparez-votre-sejour.tsx
 *
 * @param param0
 * @returns
 */
export const Breadcrumb = ({ pathsInput }: IBreadcrumbProps) => {
  const { sanityTranslations } = useStaticQuery<GetTranslatesQuery>(QUERY);
  const { lang, paths } = usePageContext();
  const usablePaths = pathsInput ?? paths;
  if (!usablePaths) return null;

  const pathsReadyToDisplay = pathnameToObjectWithHomeAndBack(
    usablePaths,
    lang,
    {
      label: sanityTranslations?._rawBreadcrumbHomeButton?.[lang],
      url: '/',
    },
    { label: sanityTranslations?._rawBreadcrumbBackButton?.[lang], url: '..' }
  );

  return (
    <ol className={styles.mainWrapper}>
      {pathsReadyToDisplay.map((path, index) => {
        if (index === pathsReadyToDisplay.length - 1)
          return <li key={`${path.url}=${path.label}`}>{path.label}</li>;
        return (
          <li key={`${path.url}=${path.label}`}>
            <LocaleLink to={path.url}>
              {path.url === '..' ? (
                <>
                  <FontAwesomeIcon icon={faArrowLeft} className={styles.icon} />
                  {path.label}
                </>
              ) : (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>{path.label}</>
              )}
            </LocaleLink>
          </li>
        );
      })}
    </ol>
  );
};

const QUERY = graphql`
  query getTranslates {
    sanityTranslations {
      _rawBreadcrumbBackButton
      _rawBreadcrumbHomeButton
    }
  }
`;
